import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SIGN_IN_CLIENT_MUTATION, UPDATE_USER } from '../../graphql/user/mutations';
import { useContextStore } from '../context/useContextStore';
import { Locale, type MutationsignInClientArgs } from '../../generated/graphql';
import { showToast } from '../../utils/toastHelpers';
import { useTranslation } from 'react-i18next';
import { getDeviceInfo } from '../../utils/deviceUtils';
import { logger } from '../../utils/logger';

interface UseSignInClientReturn {
  signIn: (
    credentials: MutationsignInClientArgs,
    /**
     * The language picked on the sign-in screen, and only when it was picked
     * there in this visit. The account's own preference wins over anything the
     * screen merely defaulted to — the selector reads a year-long cookie that
     * sign-up and password reset also write, so a stale one would rewrite the
     * language of every account signing in from that browser.
     */
    chosenLanguage?: 'en' | 'es'
  ) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export function useSignInClient(): UseSignInClientReturn {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const { signIn: contextSignIn, updateUserProfile } = useContextStore();

  const [signInMutation] = useMutation(SIGN_IN_CLIENT_MUTATION);
  // The raw mutation, not `useUpdateUser`: this one runs behind a sign-in the
  // user has already been handed, so it has nothing to say for itself — no
  // success toast, and no error toast either. A failure undoes itself instead.
  const [updateLocaleMutation] = useMutation(UPDATE_USER);

  /**
   * Carries the language picked on the sign-in screen into the account.
   *
   * The app takes its language from `clientUser.locale`, so the context is
   * handed the chosen one straight away and the session opens in it with no
   * flicker; the mutation only confirms it on the server. If it does not land,
   * the locale goes back to what the account actually holds and the app
   * follows — better a visible switch than a session in a language the account
   * never saved.
   */
  const persistChosenLanguage = async (accountLocale: Locale, chosen: Locale) => {
    try {
      const { data } = await updateLocaleMutation({ variables: { data: { locale: chosen } } });
      if (data?.updateUser?.__typename !== 'CreateUpdateUser') {
        throw new Error('Locale was not saved');
      }
    } catch (error) {
      updateUserProfile({ locale: accountLocale });
      logger.error({
        message: 'Failed to save the language chosen on sign-in',
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  };

  const signIn = async (
    credentials: MutationsignInClientArgs,
    chosenLanguage?: 'en' | 'es'
  ): Promise<{ success: boolean; error?: string }> => {
    // Prevent duplicate sign-in attempts
    if (isSigningIn) {
      return { success: false, error: 'Sign-in already in progress' };
    }

    setIsSigningIn(true);
    setIsLoading(true);
    setError(null);

    try {
      // Collect device information for multi-device login support
      // FCM token will be added later if notifications are enabled
      const deviceInfo = getDeviceInfo();

      // Convert DeviceInfo to device input format for sign-in
      // Note: For signInClient mutation, device parameter doesn't include email
      // (email is already in the mutation parameters)
      // fcmToken is optional and will be added later via UPDATE_FCM_DEVICE mutation
      const deviceInput = {
        platform: deviceInfo.platform,
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        deviceModel: deviceInfo.deviceModel,
        osVersion: deviceInfo.osVersion,
        appVersion: deviceInfo.appVersion,
        locale: deviceInfo.locale,
        timezone: deviceInfo.timezone,
        // fcmToken is optional - omit if not available (will be added later)
        ...(deviceInfo.fcmToken ? { fcmToken: deviceInfo.fcmToken } : {}),
      };

      const { data } = await signInMutation({
        variables: {
          email: credentials.email,
          password: credentials.password,
          device: deviceInput,
        },
      });

      if (!data?.signInClient) {
        throw new Error('Invalid response from server');
      }

      // Handle Error response
      // Note: We do NOT show a toast here. The caller is responsible for
      // surfacing the error to the user (via a toast, in-form error, etc.).
      // This matches the SP UI pattern and lets the SessionEvictedModal
      // display its own error without a redundant toast.
      if (data.signInClient.__typename === 'Error') {
        const errorMessage = data.signInClient.message || t('signInError');
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      // Handle successful response
      const { user, accessToken, refreshToken } = data.signInClient;

      const accountLocale = user?.locale === Locale.Es ? Locale.Es : Locale.En;
      const chosenLocale = chosenLanguage === 'es' ? Locale.Es : Locale.En;
      const languageChanged = Boolean(chosenLanguage) && chosenLocale !== accountLocale;

      // Update context with user and tokens
      contextSignIn({
        token: accessToken,
        refreshToken,
        user: languageChanged ? { ...user, locale: chosenLocale } : user,
      });

      // After the context, which is what stores the token this mutation needs.
      if (languageChanged) {
        void persistChosenLanguage(accountLocale, chosenLocale);
      }

      showToast(
        t('signInSuccessful', { firstName: user?.firstName, lastName: user?.lastName }),
        'success'
      );
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('signInError');
      setError(errorMessage);
      // Caller surfaces the error — see note above.
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
      setIsSigningIn(false);
    }
  };

  return {
    signIn,
    isLoading,
    error,
  };
}
